import { Box, BoxProps, Divider, IconButton, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IWikifolioDetailProps } from "src/api/server/wf-detail.types";
import { ShareWikifolioButton } from "src/components/common/buttons/share/share-wikifolio-button";
import { IconPencil } from "src/components/icons-mui/pencil";
import { IconTrade } from "src/components/icons-mui/trade";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { Button } from "src/components-mui/base/button/button";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { WatchlistButton } from "src/components-mui/common/buttons/watchlist-button/watchlist-button";
import { Tag } from "src/components-mui/common/tag/tag";
import { TraderAvatar } from "src/components-mui/common/trader-avatar/trader-avatar";
import { WfTradernameProfileLink } from "src/components-mui/common/wf-tradername-profile-link/wf-tradername-profile-link";
import {
    TOP_SECTION_TRADER_INFORMATION_TEST_ID,
    TOP_SECTION_TRADER_NAME_AND_LAST_LOGIN_TEST_ID,
} from "src/components-mui/pages/detail/top-section.test-ids";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useUserStore } from "src/stores/user-store";
import { EnumTagColor, EnumWikifolioStatus } from "src/types/common-enum";
import { isServerSide } from "src/utils/common-util";
import { getFullName } from "src/utils/user-name-util";

interface IProps extends BoxProps {
    headData: {
        metaTitle: string;
        metaDescription: string;
        url: string;
    };
    data: Pick<IWikifolioDetailProps["data"], "wikifolio" | "trader" | "tradeLink" | "editLink" | "myWikifoliosLink">;
}

export const TopSection = ({ headData, data, ...boxProps }: IProps) => {
    const { wikifolio, trader, tradeLink, editLink, myWikifoliosLink } = data;
    const { buildTraderProfileLink } = useLinkBuilder();
    const { formatDateShort } = useDateTimeFormatter();
    const userStore = useUserStore();
    const { t } = useTranslation(["common", "wf-detail"]);
    const getUserName = useGetUserName();

    const isInvestable = wikifolio.status === EnumWikifolioStatus.Investable;
    const showShareButton = wikifolio.status !== EnumWikifolioStatus.Test && wikifolio.status !== EnumWikifolioStatus.PublishingRequested;

    const currentUrl = isServerSide() ? "" : `${window.location.origin}${window.location.pathname}`;

    const hasFullNameAndNickName = !trader.isDeleted && Boolean(getFullName(trader) && trader.nickName);
    const traderProfileLink = buildTraderProfileLink(trader.nickName);

    return (
        <Box component="section" {...boxProps}>
            {userStore.isCurrentUser(trader) && (
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    sx={{ mb: 3, alignItems: { xs: "initial", sm: "center" }, justifyContent: "space-between", gap: 3 }}
                >
                    <ArrowLink href={myWikifoliosLink} color="green" sx={{ fontSize: "md" }}>
                        {t("to-my-wikifolios")}
                    </ArrowLink>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto" }, columnGap: 2 }}>
                        <IconButton
                            component="a"
                            href={editLink}
                            size="small"
                            variant="outlined"
                            aria-label={t("edit-wikifolio")}
                            sx={{ width: { xs: "100%", sm: "40px" } }}
                        >
                            <IconPencil sx={{ fontSize: "24px" }} />
                        </IconButton>
                        <Button
                            component="a"
                            href={tradeLink}
                            size="small"
                            variant="outlined"
                            startIcon={<IconTrade />}
                            sx={{ lineHeight: 1.2, "& .MuiButton-startIcon": { mr: 1, "& > svg": { fontSize: 24 } } }}
                            className="gtm-wf-dbcard__trade"
                        >
                            Trade
                        </Button>
                    </Box>
                </Stack>
            )}
            <WfHeading component="h1" fontSize={{ xs: "2xl", sm: "3xl", md: "4xl" }} sx={{ mb: 3 }}>
                {wikifolio.shortDescription}
            </WfHeading>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, rowGap: 4, columnGap: 1 }}>
                <Stack direction="row" sx={{ alignItems: "center", gap: { xs: 1, sm: 2 } }} data-test-id={TOP_SECTION_TRADER_INFORMATION_TEST_ID}>
                    <TraderAvatar href={traderProfileLink} sx={{ display: "flex" }} size={["xl", "2xl"]} trader={trader} loading="eager" />
                    <Stack sx={{ overflow: "hidden", justifyContent: "center" }} data-test-id={TOP_SECTION_TRADER_NAME_AND_LAST_LOGIN_TEST_ID}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "auto 1fr",
                                fontSize: { xs: "md", sm: "lg", md: "xl" },
                                mb: 0.5,
                                "& > a, & > span": { fontSize: "inherit" },
                            }}
                        >
                            <WfTradernameProfileLink href={traderProfileLink} name={getUserName(trader)} isDeleted={trader.isDeleted} />

                            {hasFullNameAndNickName && (
                                <Typography
                                    noWrap
                                    sx={{
                                        display: { xs: "block", sm: "none", md: "block" },
                                        fontSize: "inherit",
                                        lineHeight: "inherit",
                                        wordBreak: "break-all",
                                    }}
                                >
                                    &nbsp;{`| ${trader.nickName}`}
                                </Typography>
                            )}
                        </Box>
                        <Typography sx={{ color: "gray.400", fontSize: "xs", lineHeight: 1.5 }}>
                            {t("last-login")}: {formatDateShort(trader.lastLogin)}
                        </Typography>
                        {trader.isSelfRegulatedAssetManager && (
                            <Tag
                                label={t("sro")}
                                tooltip={trader.selfRegulatedAssetManagerTooltip}
                                popoverSlotProps={{ popper: { zIndex: 10 } }}
                                color={EnumTagColor.Gray}
                                sx={{
                                    alignSelf: "flex-start",
                                    mt: 0.5,
                                    px: 1,
                                    py: 0,
                                    height: "14px",
                                    minWidth: 0,
                                    lineHeight: "12px",
                                    borderColor: "gray.400",
                                    fontSize: "2xs",
                                }}
                            />
                        )}
                    </Stack>
                    {trader.profileLogoData && (
                        <Box
                            component="img"
                            src={trader.profileLogoData}
                            alt={t("company-logo-of", { ns: "wf-detail", companyName: trader?.companyName })}
                            sx={{ ml: { xs: 2, sm: 1 }, maxHeight: "48px", maxWidth: "128px" }}
                        />
                    )}
                </Stack>
                <Box
                    sx={{
                        display: "grid",
                        alignItems: "center",
                        gridTemplateColumns: isInvestable ? { xs: "1fr 1fr", sm: "auto auto" } : { xs: "1fr", sm: "auto" },
                        columnGap: 2,
                    }}
                >
                    {showShareButton && (
                        <ShareWikifolioButton url={currentUrl} metaTitle={headData.metaTitle} metaDescription={headData.metaDescription} />
                    )}
                    {isInvestable && (
                        <WatchlistButton
                            size="small"
                            wikifolioId={wikifolio.id}
                            wikifolioShortDescription={wikifolio.shortDescription}
                            isOnWatchlist={wikifolio.isOnWatchlist}
                        />
                    )}
                </Box>
            </Box>
            <Divider sx={{ mt: 3, borderColor: "gray.100", display: { xs: "block", sm: "none" } }} />
        </Box>
    );
};
